import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import en from "./locales/en";
import fr from "./locales/fr";
import nl from "./locales/nl";
import de from "./locales/de";
import ro from "./locales/ro";
import pl from "./locales/pl";
import tr from "./locales/tr";
import ar from "./locales/ar";
import es from "./locales/es";
import it from "./locales/it";
import pt from "./locales/pt";

const RESOURCES = { en, fr, nl, de, ro, pl, tr, ar, es, it, pt };

export const LANGUAGES = [
  { code: "en", label: "English", cc: "gb" },
  { code: "fr", label: "Français", cc: "fr" },
  { code: "nl", label: "Nederlands", cc: "nl" },
  { code: "es", label: "Español", cc: "es" },
  { code: "it", label: "Italiano", cc: "it" },
  { code: "pt", label: "Português", cc: "pt" },
  { code: "de", label: "Deutsch", cc: "de" },
  { code: "ro", label: "Română", cc: "ro" },
  { code: "pl", label: "Polski", cc: "pl" },
  { code: "tr", label: "Türkçe", cc: "tr" },
  { code: "ar", label: "العربية", cc: "sa" },
];

const RTL = ["ar"];
const STORAGE_KEY = "imperium-lang";
let langAnnounced = false;

function detectBrowser() {
  try {
    const list = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"]);
    for (const l of list) {
      const code = (l || "").slice(0, 2).toLowerCase();
      if (RESOURCES[code]) return code;
    }
  } catch { /* noop */ }
  return null;
}

function detect() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && RESOURCES[saved]) return saved;
  } catch { /* noop */ }
  return detectBrowser() || "en";
}

function resolve(obj, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const autoPick = useRef(null);
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && RESOURCES[saved]) return saved;
    } catch { /* noop */ }
    const d = detectBrowser();
    // Only announce when we auto-pick a non-English language (English is the silent default).
    autoPick.current = d && d !== "en" ? d : null;
    return d || "en";
  });

  // Premium one-time toast confirming the auto-detected language, with the country flag.
  useEffect(() => {
    if (!autoPick.current || langAnnounced) return;
    langAnnounced = true;
    const meta = LANGUAGES.find((x) => x.code === autoPick.current);
    if (!meta) return;
    const note = resolve(RESOURCES[autoPick.current], "langAuto") || resolve(RESOURCES.en, "langAuto") || "";
    toast(
      <div className="flex items-center gap-2.5" data-testid="lang-auto-toast">
        <img src={`https://flagcdn.com/${meta.cc}.svg`} alt="" className="w-6 h-4 rounded-[2px] object-cover shrink-0 ring-1 ring-white/15" />
        <div className="leading-tight">
          <div className="font-semibold text-sm">{meta.label}</div>
          {note ? <div className="text-xs opacity-70">{note}</div> : null}
        </div>
      </div>,
      { duration: 4500 }
    );
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", RTL.includes(lang) ? "rtl" : "ltr");
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
  }, [lang]);

  const setLang = useCallback((l) => { if (RESOURCES[l]) setLangState(l); }, []);

  const t = useCallback((key, vars) => {
    let val = resolve(RESOURCES[lang], key);
    if (val === undefined) val = resolve(RESOURCES.en, key);
    if (val === undefined) val = resolve(RESOURCES.fr, key);
    if (val === undefined) return key;
    if (vars && typeof val === "string") {
      Object.keys(vars).forEach((k) => { val = val.replace(new RegExp(`{{${k}}}`, "g"), vars[k]); });
    }
    return val;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ t, lang, setLang, dir: RTL.includes(lang) ? "rtl" : "ltr", languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) return { t: (k) => k, lang: "fr", setLang: () => {}, dir: "ltr", languages: LANGUAGES };
  return ctx;
};
